import { memo, useCallback } from 'react';
import type { Position } from '@view-model/models/common/types/ui';
import type { ModelCommentThread } from '../../domain';
import { DraggableContainer } from '@view-model/models/common/components/DraggableContainer';
import type { ModelCommentThreadId } from 'balus-common';
import type { DragContext } from '@model-framework/ui';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { generateDefaultUserIcon } from '@framework/utils';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

const COMMENT_ICON_DIAMETER = 80;
const RADIUS = COMMENT_ICON_DIAMETER / 2;
// アイコン同士の重なりを持たせるオフセット
const ICON_OFFSET = 48;
const MAX_ICONS = 3;
// アバター/チェックマークは背景円より一回り小さく描画する。複数アイコン時はこの隙間から
// 背景色を覗かせて個々のアバターを分離し、単体時は背景色（未読）や縁取り（アクティブ）をリングとして見せる
const AVATAR_INSET = 10;
// アクティブ（ホバー/選択中）時: 背景は白のまま、この太さの縁取りを付ける
const ACTIVE_BORDER_WIDTH = 6;
const HIGHLIGHT_COLOR = '#4299E1';
const DEFAULT_BG_COLOR = '#ffffff';
// 解決済みチェックマークの色（read/active 用。unread は背景が同系色になるため白にする）
const RESOLVED_CHECK_COLOR = '#01ABC1';
// 円弧をベジエ曲線で近似する際の制御点オフセット係数
const CIRCLE_BEZIER_KAPPA = 0.5523;

/** ピンの表示状態。read/unread より isActive（ホバー/選択中）を優先する */
type PinMode = 'active' | 'unread' | 'read';

function getPinMode(isRead: boolean, isActive: boolean): PinMode {
  if (isActive) return 'active';
  return isRead ? 'read' : 'unread';
}

/** スレッドの参加者ユーザーID一覧を返す（重複排除・最大MAX_ICONS件） */
function getParticipantUserIds(thread: ModelCommentThread): string[] {
  const ids = [thread.getPrimaryComment().userId, ...thread.getReplyComments().map((c) => c.userId)];
  return [...new Set(ids)].slice(0, MAX_ICONS);
}

/** アイコングループの幅を返す */
export function getCommentIconGroupWidth(thread: ModelCommentThread): number {
  if (thread.isResolved()) return COMMENT_ICON_DIAMETER;
  return COMMENT_ICON_DIAMETER + (getParticipantUserIds(thread).length - 1) * ICON_OFFSET;
}

/**
 * ピン全体の背景パス（幅 width・高さ height のカプセル形）を返す。
 * 左端だけ丸めずに直角にカットし、吹き出しの尻尾のような見た目にする。
 * width === height のとき単体アイコン用の円（左下だけ角カット）に一致する。
 */
function pinBackgroundPath(width: number, height: number): string {
  const r = height / 2;
  const k = r * CIRCLE_BEZIER_KAPPA;
  const rightCapStart = width - r;
  return [
    `M0,${r}`,
    `C0,${r - k} ${r - k},0 ${r},0`,
    `H${rightCapStart}`,
    `C${rightCapStart + k},0 ${width},${r - k} ${width},${r}`,
    `C${width},${r + k} ${rightCapStart + k},${height} ${rightCapStart},${height}`,
    `H0`,
    `V${r}`,
    'Z',
  ].join(' ');
}

// faCheck.icon[0] / [1] に FA 定義の viewBox 幅・高さが入っている
const CHECK_ICON_WIDTH = faCheck.icon[0] as number;
const CHECK_ICON_HEIGHT = faCheck.icon[1] as number;
// チェックマークを収める正方形のサイズ（COMMENT_ICON_DIAMETER の 45%）
const CHECK_FIT_SIZE = COMMENT_ICON_DIAMETER * 0.45;
// アスペクト比を保ちつつ CHECK_FIT_SIZE に収まるスケール
const CHECK_SCALE = Math.min(CHECK_FIT_SIZE / CHECK_ICON_WIDTH, CHECK_FIT_SIZE / CHECK_ICON_HEIGHT);
// スケール後の実サイズで円の中心に合わせるオフセット
const CHECK_OFFSET_X = RADIUS - (CHECK_ICON_WIDTH * CHECK_SCALE) / 2;
const CHECK_OFFSET_Y = RADIUS - (CHECK_ICON_HEIGHT * CHECK_SCALE) / 2;

type ResolvedCheckProps = {
  color: string;
};

/** resolved 状態を示すチェックマーク（背景色の上に重ねて描画） */
const ResolvedCheck: React.FC<ResolvedCheckProps> = ({ color }: ResolvedCheckProps) => (
  <g transform={`translate(${CHECK_OFFSET_X}, ${CHECK_OFFSET_Y}) scale(${CHECK_SCALE})`}>
    <path d={faCheck.icon[4] as string} fill={color} />
  </g>
);

// ---

type AvatarProps = {
  userId: string;
  xOffset: number;
  clipId: string;
  radius: number;
};

/** 1ユーザー分の円形アバターを、背景円より一回り小さく描画するサブコンポーネント */
const ParticipantAvatar: React.FC<AvatarProps> = ({ userId, xOffset, clipId, radius }: AvatarProps) => {
  const profile = useUserPublicProfile(userId);
  const inset = RADIUS - radius;
  const size = radius * 2;

  return (
    <g transform={`translate(${xOffset + inset}, ${inset})`}>
      <defs>
        <clipPath id={clipId}>
          <circle cx={radius} cy={radius} r={radius} />
        </clipPath>
      </defs>

      {profile?.imageUrl ? (
        <image
          href={profile.imageUrl}
          x={0}
          y={0}
          width={size}
          height={size}
          clipPath={`url(#${clipId})`}
          preserveAspectRatio="xMidYMid slice"
        />
      ) : (
        <g clipPath={`url(#${clipId})`}>
          {profile ? (
            generateDefaultUserIcon({
              id: profile.id,
              name: profile.name,
              isAnonymous: profile.isAnonymous,
              size,
            })
          ) : (
            <circle cx={radius} cy={radius} r={radius} fill="#9E9E9E" />
          )}
        </g>
      )}
    </g>
  );
};

// ---

type Props = {
  position: Position;
  commentThread: ModelCommentThread;
  isRead: boolean;
  isActive: boolean;
  readonly: boolean;
  onDragStart(id: ModelCommentThreadId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onClick(id: ModelCommentThreadId, e: React.MouseEvent<SVGGElement>): void;
};

export const ModelCommentIconView = memo(
  ({ position, commentThread, isRead, isActive, readonly, onDragStart, onDrag, onDragEnd, onClick }: Props) => {
    const threadId = commentThread.id;
    const isResolved = commentThread.isResolved();
    const participantUserIds = getParticipantUserIds(commentThread);
    const width = getCommentIconGroupWidth(commentThread);
    const mode = getPinMode(isRead, isActive);

    // active: 白背景＋青縁取り（読了状態を問わずホバー/選択中であることを示す）
    // unread: 背景をブランドカラーで塗りつぶす
    // read: 白背景・縁取りなし
    const backgroundFill = mode === 'unread' ? HIGHLIGHT_COLOR : DEFAULT_BG_COLOR;
    const backgroundStroke = mode === 'active' ? HIGHLIGHT_COLOR : undefined;
    // 未読時は背景がブランドカラーで塗りつぶされるため、同系色のチェックだと視認できず白にする
    const checkColor = mode === 'unread' ? 'white' : RESOLVED_CHECK_COLOR;

    const avatarRadius = RADIUS - AVATAR_INSET;

    const handleDragStart = useCallback(
      (e: React.MouseEvent<SVGGElement>) => onDragStart(threadId, e),
      [onDragStart, threadId]
    );

    const handleClick = useCallback((e: React.MouseEvent<SVGGElement>) => onClick(threadId, e), [onClick, threadId]);

    const shadowFilterId = `comment-group-shadow-${threadId}`;

    const iconContent = (
      <g style={{ cursor: readonly ? 'default' : 'move' }}>
        <defs>
          <filter id={shadowFilterId} x="-15%" y="-25%" width="130%" height="155%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodColor="#000" floodOpacity="0.22" />
          </filter>
        </defs>

        {/* 背景（左端だけ角カットした吹き出し形状）＋影 */}
        <path
          d={pinBackgroundPath(width, COMMENT_ICON_DIAMETER)}
          fill={backgroundFill}
          stroke={backgroundStroke}
          strokeWidth={backgroundStroke ? ACTIVE_BORDER_WIDTH : undefined}
          filter={`url(#${shadowFilterId})`}
        />

        {isResolved ? (
          <ResolvedCheck color={checkColor} />
        ) : (
          [...participantUserIds].reverse().map((userId, reversedIndex) => {
            const index = participantUserIds.length - 1 - reversedIndex;
            return (
              <ParticipantAvatar
                key={userId}
                userId={userId}
                xOffset={index * ICON_OFFSET}
                clipId={`comment-icon-clip-${threadId}-${index}`}
                radius={avatarRadius}
              />
            );
          })
        )}
      </g>
    );

    return (
      <DraggableContainer
        position={position}
        onDragMoveStarted={handleDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onClick={handleClick}
        dragHandleContent={iconContent}
      >
        {null}
      </DraggableContainer>
    );
  }
);
ModelCommentIconView.displayName = 'ModelCommentIconView';
